import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { useApp } from "@/contexts/AppContext";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

const BRANDS = ["All", "Blackview", "Apple", "Samsung", "Xiaomi", "TECNO", "Infinix"];

export default function PhonesPage() {
  const { lang, tr } = useApp();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const brand = params.get("brand") || "All";
  const q = params.get("q") || "";
  const [qLocal, setQLocal] = useState(q);

  useEffect(() => {
    document.title = "Phones | AFC TELECOM";
  }, []);

  useEffect(() => {
    setLoading(true);
    const query = { type: "phone" };
    if (q) query.q = q;
    api.get("/products", { params: query })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [q]);

  const filtered = useMemo(() => {
    if (brand === "All") return products;
    return products.filter((p) => (p.brand || "").toLowerCase() === brand.toLowerCase());
  }, [products, brand]);

  const setBrand = (b) => {
    const p = new URLSearchParams(params);
    if (b === "All") p.delete("brand"); else p.set("brand", b);
    setParams(p);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (qLocal.trim()) p.set("q", qLocal.trim()); else p.delete("q");
    setParams(p);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1E5FD9]">{lang === "ar" ? "الكتالوج" : "B2B Catalog"}</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold text-[#0B132B]">{tr.nav.phones}</h1>
        {brand === "Blackview" && (
          <p className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1E5FD9]/10 border border-[#1E5FD9]/30 text-xs font-semibold text-[#1E5FD9] tracking-[0.15em] uppercase">
            {lang === "ar" ? "موزع Blackview المعتمد" : "Blackview Authorized Distributor"}
          </p>
        )}
      </div>

      <form onSubmit={submitSearch} className="mb-6 flex gap-2 max-w-xl">
        <div className="relative flex-1">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={qLocal} onChange={(e) => setQLocal(e.target.value)} placeholder={tr.common.search} className="ps-9 rounded-full" data-testid="phones-search-input" />
        </div>
      </form>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="phone-brand-filters">
        {BRANDS.map((b) => {
          const active = brand === b;
          const isBlackview = b === "Blackview";
          return (
            <button
              key={b}
              onClick={() => setBrand(b)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                active
                  ? "bg-[#1E5FD9] text-white border-[#1E5FD9]"
                  : isBlackview
                    ? "bg-white text-[#1E5FD9] border-[#1E5FD9]/40 hover:bg-[#1E5FD9] hover:text-white"
                    : "bg-white text-slate-700 border-slate-200 hover:border-[#1E5FD9] hover:text-[#1E5FD9]"
              }`}
              data-testid={`brand-filter-${b.toLowerCase()}`}
            >
              {b}{isBlackview && !active && <span className="ms-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-[#1E5FD9]/10 uppercase tracking-wider">{lang === "ar" ? "معتمد" : "Auth."}</span>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-24 text-slate-400">{tr.common.loading}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 text-slate-500">{tr.common.noResults}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
